<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import SearchIcon from '../icons/SearchIcon.vue';
import LoadingIcon from '../icons/LoadingIcon.vue';
import ArticleListItem from '../ArticleListItem.vue';
import * as PleasedApi from '@/common/api/api.pleased';
import { SearchArticle } from '@/common/api/models.pleased';
import { debounce } from '@/vendor/pleased-sdk/util';
import {
  LETTERS_DIGITS_SPACES_REGEXP,
  MAX_ARTICLE_AUTOCOMPLETE_CHAR_NUM,
  MIN_ARTICLE_AUTOCOMPLETE_TEXT_LENGTH,
} from '@/common/config';
import * as Locale from '@/plugins/i18n';
import { store } from '@/store';
import { PleasedUserInfo } from '@/common/types';
import { reportingService } from '@/common/reporting';
import { analytics } from '@/common/logs/EventLogger';
import { BTN_EVTS } from '@/directives/trackClick';
import router from '@/router';
import { useI18n } from '@/plugins/i18n';
import { useArticleSearchCustomisation } from './useArticleSearchCustomisation';
import * as SUtils from '@/common/customisation/utils/styleUtils';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';
import { storeToRefs } from 'pinia';
import { devLog } from '@/common/debug';

interface Props {
  placeholderText?: string;
}

const props = defineProps<Props>();

const { t } = useI18n();
const customisation = useArticleSearchCustomisation();
const mockedFlowStore = useMockedFlowStore();
const { isActive: isMockedFlowActive, searchState: mockedSearchState } = storeToRefs(mockedFlowStore);

const searchInputStyle = computed(() =>
  SUtils.combineStyles(
    SUtils.createTextStyle(customisation.input.textColor),
    SUtils.createBorderStyle(customisation.input.borderColor),
    SUtils.createBackgroundStyle(customisation.input.fieldBackgroundColor),
    {
      ...(customisation.input.placeholderColor?.value
        ? { '--placeholder-color': customisation.input.placeholderColor.value }
        : {}),
    }
  )
);

const searchIconStyle = computed(() => SUtils.createTextStyle(customisation.input.iconColor));

const dropdownStyle = computed(() => SUtils.createBackgroundStyle(customisation.dropdown.backgroundColor));

const dropdownTitleStyle = computed(() => SUtils.createTextStyle(customisation.dropdown.titleColor));

const dropdownNoDataStyle = computed(() => SUtils.createTextStyle(customisation.dropdown.noDataColor));

const dropdownLoaderStyle = computed(() => SUtils.createTextStyle(customisation.dropdown.loaderColor));

const placeholderText = computed(
  () => props.placeholderText || customisation.input.placeholderText?.value || 'Search articles...'
);

const isVisible = computed(() => customisation.input.isVisible?.value !== false);

const searchQuery = ref('');
const showDropdown = ref(false);
const loading = ref(false);
const searchData = ref<SearchArticle[]>([]);

const pleasedUser = computed(() => store.state.user.pleasedUser as PleasedUserInfo);
const searchHistory = computed(
  () => store.state.articleAutocomplete.searchHistory as { [key: string]: SearchArticle[] }
);

const validQueryText = computed(() => {
  const searchText = searchQuery.value?.trim();
  return searchText && searchText.length >= MIN_ARTICLE_AUTOCOMPLETE_TEXT_LENGTH;
});

const dropdownData = computed(() => {
  const currentLang = Locale.i18n.locale as Locale.SupportedLanguages;
  const key = `${pleasedUser.value?.userId}-${currentLang}`;
  const searchedData = searchHistory.value[key] || [];
  return validQueryText.value ? searchData.value : searchedData;
});

const isEmpty = computed(() => dropdownData.value.length === 0);

const debouncedFetchSearchData = debounce(async () => {
  const searchText = searchQuery.value?.trim();

  // The query can shrink below the minimum while the debounced call is still pending
  // (mobile browsers also delay timers of a backgrounded page), so re-check it here.
  if (!searchText || searchText.length < MIN_ARTICLE_AUTOCOMPLETE_TEXT_LENGTH) {
    loading.value = false;
    return;
  }

  loading.value = true;
  try {
    const articles = await PleasedApi.getArticleSearch.cached({
      searchText,
    });
    searchData.value = articles.data;
  } catch {
    // API failures are already reported by the api layer; the dropdown just shows no data.
    searchData.value = [];
  } finally {
    loading.value = false;
  }
}, 500);

const handleSearchInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  let value = target.value;

  value = value.replace(LETTERS_DIGITS_SPACES_REGEXP, '');

  if (value.length > MAX_ARTICLE_AUTOCOMPLETE_CHAR_NUM) {
    value = value.substring(0, MAX_ARTICLE_AUTOCOMPLETE_CHAR_NUM);
  }

  searchQuery.value = value;

  const trimmedValue = value?.trim();

  if (trimmedValue && trimmedValue.length >= MIN_ARTICLE_AUTOCOMPLETE_TEXT_LENGTH) {
    loading.value = true;
    debouncedFetchSearchData();
  } else {
    debouncedFetchSearchData.cancel();
    loading.value = false;
    searchData.value = [];
  }
};

const handleFocus = () => {
  showDropdown.value = true;
};

const handleBlur = () => {
  setTimeout(() => {
    showDropdown.value = false;
  }, 200);
};

const openArticle = (article: SearchArticle) => {
  debouncedFetchSearchData.cancel();
  reportingService.selectedArticleFromSearch(article.id);
  analytics.trackClick({
    name: validQueryText.value ? BTN_EVTS.SEARCH_ARTICLE_RESULT : BTN_EVTS.SEARCH_ARTICLE_RECENT_SEARCH_ARTICLE,
    selection: article.id,
  });
  PleasedApi.saveArticleHit({ articleId: article.id, searchText: searchQuery.value });

  store.commit('articleAutocomplete/addSearchHistoryItem', {
    searchHistoryItem: article,
    userId: pleasedUser.value?.userId,
  });

  router.push({
    name: 'new-article',
    params: {
      id: String(article.id),
    },
  });

  showDropdown.value = false;
  searchQuery.value = '';
};

const applyMockedSearchState = () => {
  if (isMockedFlowActive.value && mockedSearchState.value) {
    devLog.debug('[ARTICLE_SEARCH] Applying mocked search state', mockedSearchState.value);
    searchQuery.value = mockedSearchState.value.searchQuery;
    searchData.value = mockedSearchState.value.searchResults;
    showDropdown.value = mockedSearchState.value.showDropdown;
  }
};

const clearSearch = () => {
  debouncedFetchSearchData.cancel();
  showDropdown.value = false;
  loading.value = false;
  searchQuery.value = '';
  searchData.value = [];
};

// React to registry changes on the same page (Help ↔ Article search): open search when
// switching to ARTICLE_SEARCH, close when switching to HELP_PAGE.
watch(mockedSearchState, (state) => {
  if (state) {
    applyMockedSearchState();
  } else {
    clearSearch();
  }
});

onMounted(() => {
  applyMockedSearchState();
});

onBeforeUnmount(() => {
  debouncedFetchSearchData.cancel();
});
</script>

<template>
  <div v-if="isVisible" class="input-radius search-container">
    <input
      type="text"
      class="input-radius search-input theme-font-main"
      :style="searchInputStyle"
      :placeholder="placeholderText"
      :value="searchQuery"
      aria-label="Search for help articles"
      @input="handleSearchInput"
      @focus="handleFocus"
      @blur="handleBlur"
    />
    <SearchIcon class="search-icon" :style="searchIconStyle" />

    <div
      v-if="showDropdown"
      class="search-dropdown shadow-soft"
      :class="{ isLoading: loading, noData: isEmpty }"
      :style="dropdownStyle"
    >
      <LoadingIcon v-if="loading" class="dropdown-loader" :style="dropdownLoaderStyle" />

      <span v-if="!loading && !validQueryText && !isEmpty" class="recent-searches-title" :style="dropdownTitleStyle">
        {{ t('component.article_autocomplete.recent_search') }}
      </span>
      <span v-if="!loading && validQueryText && !isEmpty" class="search-results-title" :style="dropdownTitleStyle">
        {{ t('component.article_autocomplete.search_results') }}
      </span>
      <ul v-if="!isEmpty">
        <ArticleListItem
          v-for="item in dropdownData"
          :key="item.id"
          :item="item"
          :highlight-query="validQueryText ? searchQuery : null"
          :highlight-color="customisation.searchItem.highlightColor?.value"
          :border-color="customisation.searchItem.borderColor?.value"
          :background-color="customisation.searchItem.backgroundColor?.value"
          :text-color="customisation.searchItem.textColor?.value"
          @mousedown="openArticle(item)"
        />
      </ul>
      <div v-else class="no-data" :style="dropdownNoDataStyle">
        <span v-if="!validQueryText && isEmpty">
          {{ t('component.article_autocomplete.no_historical_data') }}
        </span>
        <span v-else>
          {{ t('component.article_autocomplete.no_data') }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/variable.scss';

// Fallback colors for old chatbot that doesn't use customisation
.search-container {
  position: relative;
  width: 100%;
}

.search-input {
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: none;
  font-size: 16px;
  color: #333; // Fallback for old chatbot

  &::placeholder {
    color: var(--placeholder-color, #999); // Fallback for old chatbot
  }
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
}

.search-results-title {
  font-size: 14px;
  font-weight: bold;
  color: $article-autocomplete-dropdown-recent-searches-color; // Fallback for old chatbot
  padding: 16px;
  margin-bottom: 0;
}

.search-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  padding: 16px 0;
  border-radius: 4px;
  background-color: $article-autocomplete-dropdown-background; // Fallback for old chatbot
  max-height: 250px;
  overflow-y: auto;
  z-index: 1000;

  &.isLoading {
    min-height: 100px;
    justify-content: center;
    align-items: center;
    padding: 16px 0;
  }

  .dropdown-loader {
    width: 32px;
    height: 32px;
    color: $article-autocomplete-dropdown-recent-searches-color; // Fallback for old chatbot
  }

  .recent-searches-title {
    font-size: 14px;
    font-weight: bold;
    color: $article-autocomplete-dropdown-recent-searches-color; // Fallback for old chatbot
    padding: 0 16px 12px;
  }

  ul {
    list-style-type: none;
    padding: 0;
    margin: 0;
    transition: all 0.2s ease-in-out;
    opacity: 1;
  }

  &.isLoading ul,
  &.isLoading .no-data,
  &.isLoading .recent-searches-title,
  &.isLoading .search-results-title {
    display: none;
  }

  .no-data {
    display: flex;
    align-items: center;
    flex: 1;
    padding: 0 16px;
    font-size: 16px;
    color: $article-autocomplete-dropdown-no-data-color; // Fallback for old chatbot
  }
}
</style>
